import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Building } from 'lucide-react';
import { toast } from 'sonner';
import { formatInvCurrency, formatInvDate, calcProfitLoss } from './investments/investmentUtils';
import { INVESTMENT_TYPE_LABELS, TYPE_COLORS } from './investments/investmentTableConstants';
import EntityGroupedView from './investments/EntityGroupedView';
import InvestmentRow from './investments/InvestmentRow';
import logger from '../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const getGrouped = (sorted, byType, byEntity) => {
  if (byType) return sorted.reduce((acc, inv) => { (acc[inv.type] = acc[inv.type] || []).push(inv); return acc; }, {});
  if (byEntity) {
    const g = { direct: [], indirect: {}, related: {} };
    sorted.forEach(inv => { const ot = inv.owner_type || 'direct'; if (ot === 'direct') g.direct.push(inv); else { const bucket = ot === 'indirect' ? g.indirect : g.related; (bucket[inv.entity_id] = bucket[inv.entity_id] || []).push(inv); } });
    return g;
  }
  return { all: sorted };
};

const TableHeader6Col = ({ showType }) => (
  <TableHeader><TableRow>
    <TableHead className="font-medium text-slate-700">Investment</TableHead>
    {showType && <TableHead className="font-medium text-slate-700">Type</TableHead>}
    <TableHead className="font-medium text-slate-700 text-right">Invested</TableHead>
    <TableHead className="font-medium text-slate-700 text-right">Current Value</TableHead>
    <TableHead className="font-medium text-slate-700 text-right">P&L</TableHead>
    <TableHead className="font-medium text-slate-700">Details</TableHead>
    <TableHead className="font-medium text-slate-700 w-12"></TableHead>
  </TableRow></TableHeader>
);

const InvestmentsList = ({ investments, onEdit, onRefresh, groupByType = false, groupByEntity = false, isReadOnly = false, onReadOnlyAction }) => {
  const [deletingId, setDeletingId] = useState(null);
  const [entities, setEntities] = useState([]);
  const [relatedAssets, setRelatedAssets] = useState([]);

  useEffect(() => {
    if (!groupByEntity) return;
    const token = localStorage.getItem('auth_token');
    const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
    Promise.all([
      fetch(`${BACKEND_URL}/api/entities`, { headers, cache: 'no-store' }).then(r => r.ok ? r.json() : []),
      fetch(`${BACKEND_URL}/api/related-assets`, { headers, cache: 'no-store' }).then(r => r.ok ? r.json() : []),
    ]).then(([e, a]) => { setEntities(e); setRelatedAssets(a); }).catch(err => logger.error('Failed to load entities/assets:', err));
  }, [groupByEntity]);

  const handleDelete = async (investmentId) => {
    if (!window.confirm('Are you sure you want to delete this investment?')) return;
    setDeletingId(investmentId);
    try {
      const res = await fetch(`${BACKEND_URL}/api/investments/${investmentId}`, { method: 'DELETE' });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Delete failed'); }
      toast.success('Investment deleted successfully!'); onRefresh();
    } catch (error) { logger.error('Delete error:', error); toast.error(error.message || 'Failed to delete investment'); }
    finally { setDeletingId(null); }
  };

  if (!investments?.length) {
    return <Card className="portfolio-card"><CardContent className="p-12 text-center"><div className="space-y-4"><div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto"><Building className="w-8 h-8 text-slate-400" /></div><h3 className="text-lg font-serif font-bold text-slate-900">No investments yet</h3><p className="body-text">Start building your portfolio by adding your first investment.</p></div></CardContent></Card>;
  }

  const sorted = [...investments].sort((a, b) => new Date(b.purchase_date || b.created_at) - new Date(a.purchase_date || a.created_at));
  const grouped = getGrouped(sorted, groupByType, groupByEntity);
  const renderRow = (inv) => <InvestmentRow key={inv.investment_id} investment={inv} groupByType={groupByType} isReadOnly={isReadOnly} onReadOnlyAction={onReadOnlyAction} onEdit={onEdit} onDelete={handleDelete} deletingId={deletingId} />;

  if (groupByType) {
    return (
      <div className="space-y-8">
        {Object.entries(grouped).map(([type, typeInvs]) => (
          <Card key={type} className="portfolio-card">
            <CardHeader className={`${TYPE_COLORS[type] || 'bg-slate-100'} rounded-t-lg`}>
              <div className="flex justify-between items-center"><CardTitle className="text-xl font-serif">{INVESTMENT_TYPE_LABELS[type] || type}</CardTitle><Badge variant="secondary" className="font-mono bg-white/90">{typeInvs.length} {typeInvs.length === 1 ? 'Investment' : 'Investments'}</Badge></div>
            </CardHeader>
            <CardContent className="pt-6"><div className="overflow-x-auto"><Table><TableHeader6Col showType={false} /><TableBody>{typeInvs.map(renderRow)}</TableBody></Table></div></CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (groupByEntity) return <EntityGroupedView groupedInvestments={grouped} entities={entities} relatedAssets={relatedAssets} renderInvestmentRow={renderRow} />;

  return (
    <Card className="portfolio-card">
      <CardHeader><div className="flex justify-between items-center"><CardTitle className="text-xl font-serif">Investment Portfolio</CardTitle><Badge variant="outline" className="font-mono">{investments.length} {investments.length === 1 ? 'Investment' : 'Investments'}</Badge></div></CardHeader>
      <CardContent><div className="overflow-x-auto"><Table><TableHeader6Col showType /><TableBody>{sorted.map(renderRow)}</TableBody></Table></div></CardContent>
    </Card>
  );
};

export default InvestmentsList;
